/**
 * ColourMatch Pro · Lightweight Registration.
 *
 * Founder direction 2026-02-09: painting businesses register with basic
 * details and access ColourMatch Pro immediately. No approval gate. Any
 * remaining business-profile fields (gallery, service areas, credentials,
 * public listing) are completed later inside Business Profile.
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import usePageMeta from "@/hooks/usePageMeta";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProRegister() {
  usePageMeta({
    title: "Register your business — ColourMatch Pro",
    description: "Register your painting and decorating business on ColourMatch Pro and access the professional workspace immediately.",
    path: "/pro/register",
  });
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    first_name: "", last_name: "", company_name: "",
    email: "", phone: "", business_address: "", password: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid =
    form.first_name.trim() && form.last_name.trim() && form.company_name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
    form.phone.trim().length >= 6 && form.business_address.trim().length >= 4 &&
    form.password.length >= 8;

  const submit = async (e) => {
    e?.preventDefault();
    setErr(null);
    if (!valid) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/pro/register`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        company_name: form.company_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        business_address: form.business_address.trim(),
        password: form.password,
      }, { withCredentials: true });
      // Persist token so AuthContext picks it up
      if (data?.token) {
        try { localStorage.setItem("cm_token", data.token); } catch { /* ignore */ }
      }
      await refresh?.();
      toast.success("Welcome to ColourMatch Pro.");
      nav("/pro", { replace: true });
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Registration failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg py-10 px-4 flex flex-col" data-testid="pro-register-page">
      <div className="max-w-lg w-full mx-auto">
        <Link to="/" className="inline-block mb-8">
          <div className="font-serif text-2xl text-brand-ink">
            ColourMatch<span className="text-brand-teal"> Pro</span>
          </div>
          <div className="text-[11px] text-brand-muted mt-1">
            Professional workspace for painting and decorating businesses
          </div>
        </Link>

        <div className="bg-white rounded-3xl border border-brand-linen p-6 sm:p-8">
          <h1 className="font-serif text-2xl sm:text-3xl text-brand-ink leading-tight mb-2">
            Register your business
          </h1>
          <p className="text-sm text-brand-muted mb-6 leading-relaxed">
            A few details and you&rsquo;re in. Gallery, service areas and credentials can be completed later from Business Profile.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="pro-register-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="First name" id="first_name" value={form.first_name} onChange={set("first_name")} testid="reg-first-name" required />
              <Field label="Last name"  id="last_name"  value={form.last_name}  onChange={set("last_name")}  testid="reg-last-name" required />
            </div>
            <Field label="Company name" id="company_name" value={form.company_name} onChange={set("company_name")} testid="reg-company-name" required />
            <Field label="Business email" id="email" type="email" value={form.email} onChange={set("email")} testid="reg-email" autoComplete="email" required />
            <Field label="Phone number" id="phone" type="tel" value={form.phone} onChange={set("phone")} testid="reg-phone" autoComplete="tel" required />
            <Field label="Business address" id="business_address" value={form.business_address} onChange={set("business_address")} testid="reg-address" required placeholder="Street, suburb, state, postcode" />
            <Field label="Password" id="password" type="password" value={form.password} onChange={set("password")} testid="reg-password" autoComplete="new-password" required hint="At least 8 characters" />

            <div className="text-[11px] text-brand-muted italic pt-1">
              You can add a company logo, gallery, service areas and credentials any time from Business Profile.
            </div>

            {err && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="reg-error">{err}</div>
            )}

            <Button
              type="submit"
              disabled={!valid || busy}
              data-testid="reg-submit"
              className="w-full bg-brand-navy text-white hover:bg-brand-teal h-12 font-semibold rounded-xl"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating your workspace…</> :
                <>Create ColourMatch Pro workspace <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-brand-linen text-xs text-brand-muted text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-brand-teal hover:underline" data-testid="reg-signin-link">Sign in</Link>
          </div>
        </div>

        <p className="text-[11px] text-brand-muted mt-6 max-w-md mx-auto text-center leading-relaxed">
          Registering your business gives you immediate access to ColourMatch Pro. Subscription billing is managed inside Membership &amp; Billing after you sign in.
        </p>
      </div>
    </div>
  );
}

function Field({ label, id, type = "text", value, onChange, required, hint, placeholder, autoComplete, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-brand-ink text-xs font-semibold">{label}</Label>
      <Input
        id={id} type={type} value={value} onChange={onChange}
        required={required} placeholder={placeholder} autoComplete={autoComplete}
        data-testid={testid}
        className="mt-1"
      />
      {hint && <div className="text-[11px] text-brand-muted mt-1">{hint}</div>}
    </div>
  );
}
